import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useParams, Link, useNavigate } from 'react-router-dom';
import useProductsData from '../hooks/useProductsData';
import { slugify, optimizeImage, generateSrcSet } from '../utils';
import { ArrowLeft, Download, ZoomIn, X, ChevronRight, Home, Zap } from 'lucide-react';


import SEO from '../components/SEO';
import Schema from '../components/Schema';
import { SITE_URL } from '../data/Constants';

const ProductDetailsPage = () => {
   const { slug } = useParams();
   const navigate = useNavigate();
   const { products: productsData, loading: dataLoading } = useProductsData();
   const [product, setProduct] = useState(null);
   const [activeImage, setActiveImage] = useState(0);
   const [isZoomed, setIsZoomed] = useState(false);
   const [loading, setLoading] = useState(true);

   useEffect(() => {
      if (dataLoading || !slug) return;

      // Find product by matching slugified name
      const foundProduct = productsData.find(p => slugify(p.name) === slug);

      if (foundProduct) {
         setProduct(foundProduct);
      }
      setLoading(false);
   }, [slug, dataLoading, productsData]);

   if (loading) {
      return (
         <div className="min-h-screen flex items-center justify-center pt-[72px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
         </div>
      );
   }

   if (!product) {
      return (
         <div className="min-h-screen pt-[72px] flex flex-col items-center justify-center">
            <h2 className="text-2xl font-bold text-gray-700 mb-4">Product Not Found</h2>
            <Link to="/products" className="px-6 py-3 bg-primary text-white rounded-md uppercase font-bold tracking-widest hover:bg-secondary transition-colors">
               Back to Products
            </Link>
         </div>
      );
   }



   const currentImage = product.images && product.images.length > 0 ? product.images[activeImage] : null;

   // --- GET DOWNLOADS & RESOURCES ---
   const getDownloads = () => {
      const downloads = [];

      const addDownload = (key, type) => {
         const items = product[key];
         if (!items) return;

         const list = Array.isArray(items) ? items : [items];

         list.forEach(url => {
            if (typeof url === 'string' && url.length > 0) {
               let cleanFilename = '';
               try {
                  const filename = decodeURIComponent(url.split('/').pop().split('?')[0]);
                  cleanFilename = filename
                     .replace(/\.pdf$/i, '')
                     .replace(/Specification Sheet/i, '')
                     .replace(/Installation Manual/i, '')
                     .replace(/Installation and Operation Manual/i, '')
                     .replace(/Owner's Manual/i, '')
                     .replace(/Owners Manual/i, '')
                     .replace(/Brochure/i, '')
                     .replace(/W\d+-\d+/g, '')
                     .replace(/_/g, ' ')
                     .replace(/-/g, ' ')
                     .replace(/%20/g, ' ')
                     .replace(/\s+/g, ' ')
                     .trim();
               } catch (e) { }

               let displayName = `Download ${type}`;
               if (cleanFilename && cleanFilename.length > 2) {
                  displayName = `${type} - ${cleanFilename}`;
               } else {
                  displayName = `${type} for ${product.name}`;
               }

               downloads.push({ type, url, name: displayName, cleanName: cleanFilename });
            }
         });
      };

      addDownload('spec sheet', 'Spec Sheet');
      addDownload('user manual', 'User Manual');
      addDownload('installation manual', 'Installation Manual');
      addDownload('brochure', 'Brochure');
      addDownload('manual', 'Manual');
      addDownload('CAD drawing', 'CAD Drawing');

      return downloads;
   };

   const downloads = getDownloads();

   // Parse specs for display if they are object/string mix
   // The JSON seems to have "spec sheet" as URLs, but maybe we want to extracting key features/specs from description HTML or just Key Series Features if we can parse it.
   // However, the description HTML actually contains "SPECIFICATION" and "KEY SERIES FEATURES" sections in <div>s. 
   // We will render the description HTML as is, but maybe style it better.

   // Plain text description for meta tag (strip HTML)
   const baseDescription = product.description?.replace(/<[^>]+>/g, '')?.replace(/\s+/g, ' ')?.trim() || `Detailed specifications and features.`;
   const enhancedDescription = `${product.company || 'Premium'} ${product.name} - ${baseDescription}`;

   return (
      <div className="min-h-screen pt-[74px] md:pt-[82px] bg-white animate-fade-in">
         <SEO
            title={`${product.name} | ${product.company || 'Premium'} Fireplace`}
            description={enhancedDescription.substring(0, 160)}
            canonical={`${SITE_URL}/products/${slug}`}
            type="product"
            image={product.images?.[0]}
         />
         <Schema
            type="Product"
            data={product}
         />
         <Schema
            type="BreadcrumbList"
            data={{
               items: [
                  { name: 'Home', path: '/' },
                  { name: 'Products', path: '/products' },
                  { name: product.name, path: `/products/${slug}` }
               ]
            }}
         />

         {/* Breadcrumbs / Header Stub */}
         <div className="bg-white border-b border-gray-300 py-4 px-6 md:px-12">
            <div className="max-w-7xl mx-auto flex items-center text-xs font-bold uppercase tracking-widest text-secondary gap-2">
               <Link to="/" className="hover:text-primary transition-colors">Home</Link>
               <ChevronRight className="w-3 h-3" />
               <Link to="/products" className="hover:text-primary transition-colors">Products</Link>
               <ChevronRight className="w-3 h-3" />
               <span className="text-primary">{product.name}</span>
            </div>
         </div>

         <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
            {/* Back Button */}
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-primary hover:text-black mb-8 transition-colors group">
               <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Back
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
               {/* Left: Images */}
               <div className="flex flex-col gap-6">
                  <div
                     className={`relative w-full bg-white border border-gray-200 rounded-sm shadow-sm p-4 md:p-8 flex items-center justify-center group overflow-hidden ${currentImage ? 'cursor-zoom-in' : ''}`}
                     onClick={() => currentImage && setIsZoomed(true)}
                  >
                     {currentImage ? (
                        <>
                           <img
                              src={optimizeImage(currentImage, 800)}
                              srcSet={generateSrcSet(currentImage)}
                              sizes="(max-width: 1024px) 100vw, 800px"
                              alt={product.name}
                              width={800}
                              height={800}
                              fetchPriority="high"
                              className="w-full h-auto object-contain mix-blend-multiply transition-transform duration-500 group-hover:scale-105"
                           />
                           <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/5">
                              <div className="bg-white/90 text-secondary px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest shadow-lg flex items-center gap-2 backdrop-blur-sm">
                                 <ZoomIn className="w-3 h-3" /> Zoom Image
                              </div>
                           </div>
                        </>
                     ) : (
                        <span className="text-gray-400 font-bold uppercase tracking-widest">No Image Available</span>
                     )}
                  </div>

                  {/* Thumbnails */}
                  {product.images && product.images.length > 1 && (
                     <div className="grid grid-cols-5 gap-4">
                        {product.images.map((img, idx) => (
                           <button
                              key={idx}
                              onClick={() => setActiveImage(idx)}
                              aria-label={`View product image ${idx + 1}`}
                              className={`aspect-square border-2 rounded-sm overflow-hidden transition-all ${activeImage === idx ? 'border-primary ring-2 ring-primary/20' : 'border-gray-200 hover:border-gray-400 opacity-70 hover:opacity-100'}`}
                           >
                              <img
                                 src={optimizeImage(img, 200)}
                                 srcSet={generateSrcSet(img, [200, 400])}
                                 sizes="200px"
                                 alt={`${product.name} - view ${idx + 1}`}
                                 width={200}
                                 height={200}
                                 loading={idx < 5 ? "eager" : "lazy"}
                                 className="w-full h-full object-cover"
                              />
                           </button>
                        ))}
                     </div>
                  )}
               </div>

               {/* Right: Info */}
               <div className="flex flex-col">
                  <div className="mb-2 flex items-center gap-3">
                     {product.company && (
                        <span className="px-3 py-1 bg-primary text-white text-[10px] font-bold uppercase tracking-widest rounded-sm">
                           {product.company}
                        </span>
                     )}
                     <span className='px-3 py-1 bg-black text-white text-[10px] font-bold uppercase tracking-widest rounded-sm'>
                        SKU: {product.sku}
                     </span>
                  </div>

                  <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-secondary uppercase leading-none mb-2">{product.name}</h1>

                  {/* Quick Specs Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-2 border-y border-gray-300 pt-2 pb-4">
                     {product.sizes && (
                        <div className="space-y-1">
                           <span className="text-xs text-primary font-bold uppercase tracking-widest">Available Sizes</span>
                           <div className="font-medium text-gray-700 flex flex-wrap gap-2">
                              {(Array.isArray(product.sizes) ? product.sizes : product.sizes.split(',')).map((size, i) => (
                                 <span key={i} className="px-2 py-1 bg-primary rounded text-xs font-bold text-white">
                                    {size.trim().replace(/^['"]|['"]$/g, '')}
                                 </span>
                              ))}
                           </div>
                        </div>
                     )}
                     {product['colors'] && (
                        <div className="space-y-1">
                           <span className="text-xs text-primary font-bold uppercase tracking-widest">Color</span>
                           <div className="font-medium text-gray-700 flex flex-wrap gap-2">
                              {(product['colors'].split(',')).map((color, i) => (
                                 <span key={i} className="px-2 py-1 bg-primary rounded text-xs font-bold text-white capitalize">
                                    {color.trim().replace(/^['"]|['"]$/g, '')}
                                 </span>
                              ))}
                           </div>
                        </div>
                     )}
                     {product['ignition'] && (
                        <div className="space-y-1">
                           <span className="text-xs text-primary font-bold uppercase tracking-widest">Ignition</span>
                           <div className="font-medium text-gray-700 flex flex-wrap gap-2">
                              {(product['ignition'].split(',')).map((ignition, i) => (
                                 <span key={i} className="px-2 py-1 bg-primary rounded text-xs font-bold text-white capitalize">
                                    {ignition.trim().replace(/^['"]|['"]$/g, '')}
                                 </span>
                              ))}
                           </div>
                        </div>
                     )}
                     {(product['power voltage'] || product.power_voltage) && (
                        <div className="space-y-1">
                           <span className="text-xs text-primary font-bold uppercase tracking-widest">Power</span>
                           <div className="font-medium text-gray-700 flex flex-wrap gap-2">
                              {(() => {
                                 const power = product['power voltage'] || product.power_voltage;
                                 if (typeof power === 'object' && power !== null) {
                                    return Object.keys(power).flatMap((key) => {
                                       // Split voltage string by comma only (assuming CSV format in string)
                                       // Example: "'208', '220-240'" -> ["'208'", " '220-240'"]
                                       return power[key].toString().split(',').map((v, i) => {
                                          const voltage = v.trim().replace(/^['"]|['"]$/g, '');
                                          return (
                                             <span key={`${key}-${i}`} className="px-2 py-1 bg-primary rounded text-xs font-bold text-white">
                                                {key}W {voltage}V
                                             </span>
                                          );
                                       });
                                    });
                                 } else if (typeof power === 'string') {
                                    return power.split(',').map((item, i) => (
                                       <span key={i} className="px-2 py-1 bg-gray-100 border border-gray-200 rounded text-xs font-bold text-gray-700">
                                          {item.trim().replace(/^['"]|['"]$/g, '')}V
                                       </span>
                                    ));
                                 }
                                 return power;
                              })()}
                           </div>
                        </div>
                     )}
                  </div>

                  {/* Description HTML */}
                  <div className="prose prose-sm max-w-none text-gray-600 mb-2 product-description" dangerouslySetInnerHTML={{ __html: product.description || '' }} />

                  {/* Downloads Section */}
                  {downloads.length > 0 && (
                     <div className="bg-gray-50 rounded-lg p-6 mb-8 border border-gray-200">
                        <h2 className="font-heading text-lg uppercase text-secondary font-bold mb-4 flex items-center gap-2">
                           <Download className="w-5 h-5 text-primary" /> Downloads & Resources
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                           {downloads.map((item, idx) => (
                              <a
                                 key={idx}
                                 href={item.url}
                                 target="_blank"
                                 rel="noopener noreferrer"
                                 title={`Download ${item.type} for ${product.name}`}
                                 className="flex items-center gap-3 p-3 bg-white border border-gray-200 hover:border-primary rounded-sm transition-all group hover:shadow-sm"
                              >
                                 <div className="w-8 h-8 rounded-full bg-gray-100 group-hover:bg-primary/10 flex items-center justify-center shrink-0 transition-colors">
                                    <Download className="w-4 h-4 text-gray-500 group-hover:text-primary" />
                                 </div>
                                 <div className="flex flex-col overflow-hidden">
                                    <span className="text-xs font-bold uppercase tracking-wider text-secondary truncate">{item.name}</span>
                                 </div>
                              </a>
                           ))}
                        </div>
                     </div>
                  )}

                  <div className="mt-auto pt-6">
                     <Link to="/contact" state={{ productOfInterest: product.name, sku: product.sku }} className="block w-full text-center py-4 bg-primary text-white uppercase font-bold tracking-widest rounded-xl hover:bg-secondary transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                        Request a Quote
                     </Link>
                  </div>
               </div>
            </div>
         </div>

         {/* Image Zoom Modal */}
         {isZoomed && currentImage && ReactDOM.createPortal(
            <div className="fixed inset-0 z-10000 bg-black/95 flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={() => setIsZoomed(false)}>
               <button onClick={() => setIsZoomed(false)} className='absolute top-6 right-6 z-20 p-3 bg-white/10 hover:bg-white text-white hover:text-black rounded-full transition-colors backdrop-blur-md'>
                  <X className='w-8 h-8' />
               </button>
               <img
                  src={optimizeImage(currentImage, 1600)}
                  srcSet={generateSrcSet(currentImage)}
                  sizes="95vw"
                  alt={product.name}
                  className="max-w-[95vw] max-h-[95vh] object-contain select-none shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
               />
            </div>,
            document.body
         )}
      </div>
   );
};

export default ProductDetailsPage;